import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  LogOut, Wallet, ArrowUpRight, ArrowDownRight, Calendar as CalendarIcon, 
  Cake, FileText, Heart, MessageCircle, MapPin, Users, Send, ExternalLink, Trophy,
  User, Pencil, Hand, Sparkles, Camera
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import PlayerStatsWidget from "../components/PlayerStatsWidget";
import SessionMatchesModal from "../components/SessionMatchesModal";
import LedgerDownloadButton from "../components/LedgerDownloadButton";
import ProfileEditModal from "../components/ProfileEditModal";

const UserDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  
  // Session matches modal state
  const [showSessionMatches, setShowSessionMatches] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Profile modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [specialitiesList, setSpecialitiesList] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchSpecialities();
    // Check if profile is incomplete on initial load
    if (user && !user.profile_complete) {
      setShowProfilePrompt(true);
    }
  }, []);

  const fetchSpecialities = async () => {
    try {
      const response = await apiClient.get("/specialities");
      setSpecialitiesList(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error("Failed to fetch specialities");
    }
  };

  const handleProfileUpdated = (updatedUser) => {
    // Update the local user state
    if (updateUser) {
      updateUser(updatedUser);
    }
    // Close the prompt if it was showing
    setShowProfilePrompt(false);
    // Refresh dashboard to get updated user data
    fetchDashboard();
  };

  // Helper to get speciality name by ID
  const getSpecialityName = (specId) => {
    const spec = specialitiesList.find(s => s.id === specId);
    return spec?.name || specId;
  };

  // Helper to get icon component
  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />;
  };

  const getSpecialityIcon = (specId) => {
    const spec = specialitiesList.find(s => s.id === specId);
    return spec?.icon || "Star";
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/user/dashboard");
      setDashboard(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await apiClient.post(`/posts/${postId}/like`);
      // Update local state
      setDashboard(prev => ({
        ...prev,
        posts: prev.posts.map(p => 
          p.id === postId 
            ? { 
                ...p, 
                likes: response.data.liked 
                  ? [...p.likes, user.id] 
                  : p.likes.filter(id => id !== user.id),
                like_count: response.data.like_count 
              }
            : p
        )
      }));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleComment = async (postId) => {
    const content = commentInputs[postId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await apiClient.post(`/posts/${postId}/comments`, { content });
      // Update local state
      setDashboard(prev => ({
        ...prev,
        posts: prev.posts.map(p => 
          p.id === postId 
            ? { ...p, comments: [...p.comments, response.data] }
            : p
        )
      }));
      setCommentInputs(prev => ({ ...prev, [postId]: "" }));
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Function to render content with clickable URLs
  const renderContentWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#064E3B] hover:underline inline-flex items-center gap-1"
          >
            {part}
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-[#064E3B] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const balance = dashboard?.user?.balance || 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-[#064E3B] text-white sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#BEF264] rounded-xl flex items-center justify-center">
                <span className="text-[#064E3B] font-bold text-lg">TT</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg">Turf Tribe</h1>
                <p className="text-xs text-white/70">Welcome, {dashboard?.user?.name}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={logout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Balance Card */}
          <div className="bg-white/10 backdrop-blur rounded-2xl p-5 mt-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Your Balance</p>
                <p className={`text-3xl font-heading font-black ${balance > 0 ? 'text-[#BEF264]' : balance < 0 ? 'text-red-400' : 'text-white'}`}>
                  ₹{Math.abs(balance).toFixed(2)}
                </p>
                <p className="text-white/60 text-xs mt-1">
                  {balance > 0 ? "Credit in system" : balance < 0 ? "Amount due" : "All settled"}
                </p>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${balance > 0 ? 'bg-[#BEF264]/20' : balance < 0 ? 'bg-red-400/20' : 'bg-white/20'}`}>
                <Wallet className={`w-8 h-8 ${balance > 0 ? 'text-[#BEF264]' : balance < 0 ? 'text-red-400' : 'text-white'}`} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Birthdays Banner */}
      {dashboard?.upcoming_birthdays?.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Cake className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-4">
                {dashboard.upcoming_birthdays.slice(0, 3).map((b) => (
                  <span key={b.user_id} className="text-sm text-amber-800 whitespace-nowrap">
                    <strong>{b.name}</strong> - {b.birthday_date} 
                    {b.days_until === 0 ? " 🎉 Today!" : ` (${b.days_until}d)`}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border rounded-xl p-1 mb-6 w-full grid grid-cols-5">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-lg text-xs data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-lg text-xs data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              Profile
            </TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger" className="rounded-lg text-xs data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              Ledger
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions" className="rounded-lg text-xs data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              Sessions
            </TabsTrigger>
            <TabsTrigger value="feed" data-testid="tab-feed" className="rounded-lg text-xs data-[state=active]:bg-[#064E3B] data-[state=active]:text-white">
              Feed
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 animate-fade-in">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#064E3B]/10 rounded-lg flex items-center justify-center">
                      <CalendarIcon className="w-5 h-5 text-[#064E3B]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {dashboard?.sessions_attended?.length || 0}
                      </p>
                      <p className="text-xs text-slate-500">Sessions Attended</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#064E3B]/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#064E3B]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {dashboard?.transactions?.length || 0}
                      </p>
                      <p className="text-xs text-slate-500">Transactions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Birthdays */}
            {dashboard?.upcoming_birthdays?.length > 0 && (
              <Card className="rounded-2xl border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <Cake className="w-5 h-5 text-amber-500" />
                    Upcoming Birthdays
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboard.upcoming_birthdays.map((b) => (
                      <div
                        key={b.user_id}
                        className="flex items-center justify-between p-3 bg-amber-50 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-amber-600 font-bold">
                              {b.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{b.name}</p>
                            <p className="text-sm text-amber-600">{b.birthday_date}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${b.days_until === 0 ? 'text-amber-600' : 'text-slate-500'}`}>
                          {b.days_until === 0 ? "Today! 🎉" : `${b.days_until} days`}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Player Statistics */}
            <PlayerStatsWidget limit={5} showViewMore={true} />

            {/* Recent Activity */}
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.transactions?.length === 0 ? (
                  <p className="text-slate-500 text-center py-6">No transactions yet</p>
                ) : (
                  <div className="space-y-2">
                    {dashboard?.transactions?.slice(0, 5).map((t) => (
                      <div
                        key={t.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-l-4 ${
                          t.amount >= 0 
                            ? 'bg-green-50 border-l-green-500' 
                            : 'bg-red-50 border-l-red-500'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            t.amount >= 0 ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {t.amount >= 0 
                              ? <ArrowDownRight className="w-4 h-4 text-green-600" />
                              : <ArrowUpRight className="w-4 h-4 text-red-600" />
                            }
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-900">{t.description}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(t.date).toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <p className={`font-bold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {t.amount >= 0 ? '+' : ''}₹{Math.abs(t.amount).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <User className="w-5 h-5 text-[#064E3B]" />
                      Player Profile
                    </CardTitle>
                    <CardDescription>Your pickleball player information</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowProfileModal(true)}
                    className="rounded-xl"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Photo & Basic Info */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {dashboard?.user?.photo_url ? (
                      <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[#064E3B]/20">
                        <img 
                          src={dashboard.user.photo_url} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#064E3B]/10 border-4 border-dashed border-[#064E3B]/30 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-[#064E3B]/50" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{dashboard?.user?.name}</h3>
                    <p className="text-sm text-slate-500">{dashboard?.user?.phone}</p>
                    {dashboard?.user?.email && (
                      <p className="text-sm text-slate-500">{dashboard.user.email}</p>
                    )}
                  </div>
                </div>

                {/* Playing Hand */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#064E3B]/10 rounded-lg flex items-center justify-center">
                      <Hand className="w-5 h-5 text-[#064E3B]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Playing Hand</p>
                      <p className="font-semibold text-slate-900">
                        {dashboard?.user?.playing_hand 
                          ? dashboard.user.playing_hand.charAt(0).toUpperCase() + dashboard.user.playing_hand.slice(1)
                          : <span className="text-slate-400 italic">Not set</span>
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Specialities */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#064E3B]" />
                    <p className="text-sm font-semibold text-slate-700">My Specialities</p>
                  </div>
                  {dashboard?.user?.specialities?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {dashboard.user.specialities.map((specId) => (
                        <span
                          key={specId}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#064E3B] text-white text-sm rounded-full"
                        >
                          <span className="text-[#BEF264]">
                            {getIcon(getSpecialityIcon(specId))}
                          </span>
                          {getSpecialityName(specId)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No specialities selected</p>
                  )}
                </div>

                {/* Profile Completion Status */}
                {!dashboard?.user?.profile_complete && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800 font-medium">
                      Complete your profile by adding a photo, playing hand, and specialities!
                    </p>
                    <Button
                      size="sm"
                      onClick={() => setShowProfileModal(true)}
                      className="mt-2 bg-amber-600 hover:bg-amber-700 rounded-lg"
                      data-testid="complete-profile-btn"
                    >
                      Complete Profile
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle className="font-heading">Transaction History</CardTitle>
                  <CardDescription>All your payments and deductions</CardDescription>
                </div>
                <LedgerDownloadButton
                  userName={user?.name || ""}
                  variant="outline"
                  size="sm"
                  label="Download PDF"
                  testIdPrefix="user-ledger-download"
                />
              </CardHeader>
              <CardContent>
                {dashboard?.transactions?.length === 0 ? (
                  <div className="empty-state">
                    <Wallet className="w-12 h-12 mb-3" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard?.transactions?.map((t) => (
                      <div
                        key={t.id}
                        data-testid={`transaction-${t.id}`}
                        className={`flex items-center justify-between p-4 rounded-xl border-l-4 ${
                          t.amount >= 0 
                            ? 'bg-green-50 border-l-green-500' 
                            : 'bg-red-50 border-l-red-500'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            t.amount >= 0 ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {t.amount >= 0 
                              ? <ArrowDownRight className="w-5 h-5 text-green-600" />
                              : <ArrowUpRight className="w-5 h-5 text-red-600" />
                            }
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{t.description}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(t.date).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            {t.details && (
                              <p className="text-xs text-slate-400">{t.details}</p>
                            )}
                          </div>
                        </div>
                        <p className={`text-lg font-bold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {t.amount >= 0 ? '+' : ''}₹{Math.abs(t.amount).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="animate-fade-in">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Sessions Attended</CardTitle>
                <CardDescription>Your pickleball session history - click to view match scores</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboard?.sessions_attended?.length === 0 ? (
                  <div className="empty-state">
                    <CalendarIcon className="w-12 h-12 mb-3" />
                    <p>No sessions attended yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dashboard?.sessions_attended?.map((s) => (
                      <div
                        key={s.id}
                        data-testid={`session-${s.id}`}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer hover:border-[#064E3B] hover:bg-[#064E3B]/5 transition-all"
                        onClick={() => { setSelectedSession(s); setShowSessionMatches(true); }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-[#064E3B]" />
                              <span className="font-bold text-slate-900">{s.turf_location_name}</span>
                            </div>
                            <p className="text-sm text-slate-500">
                              {new Date(s.date).toLocaleDateString('en-IN', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Your share</p>
                            <p className="text-lg font-bold text-red-600">
                              -₹{(s.user_charge || s.per_person_amount).toFixed(2)}
                            </p>
                            {s.user_guest_count > 0 && (
                              <p className="text-xs text-amber-600">
                                +{s.user_guest_count} guest{s.user_guest_count > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Users className="w-4 h-4" />
                            <span>
                              {s.total_attendees || s.attendee_names.length} heads: {s.attendee_names.join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-[#064E3B] font-medium">
                            <Trophy className="w-3 h-3" />
                            View Scores →
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feed Tab */}
          <TabsContent value="feed" className="animate-fade-in">
            <div className="space-y-4">
              {dashboard?.posts?.length === 0 ? (
                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="py-12">
                    <div className="empty-state">
                      <FileText className="w-12 h-12 mb-3" />
                      <p>No posts yet</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                dashboard?.posts?.map((p) => (
                  <Card key={p.id} data-testid={`post-${p.id}`} className="rounded-2xl border-slate-200 overflow-hidden">
                    <CardContent className="p-0">
                      {/* Post Header */}
                      <div className="p-4 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#064E3B] rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">
                              {p.author_name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{p.author_name}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(p.created_at).toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Post Content */}
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-2">{p.title}</h3>
                        <p className="text-slate-700 whitespace-pre-wrap">{renderContentWithLinks(p.content)}</p>
                        {p.url && (
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-3 text-[#064E3B] hover:underline text-sm font-medium"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {p.url}
                          </a>
                        )}
                      </div>
                      
                      {/* Post Actions */}
                      <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-4">
                        <button
                          data-testid={`like-post-${p.id}`}
                          onClick={() => handleLike(p.id)}
                          className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                            p.likes.includes(user.id) 
                              ? 'text-red-500' 
                              : 'text-slate-500 hover:text-red-500'
                          }`}
                        >
                          <Heart className={`w-5 h-5 ${p.likes.includes(user.id) ? 'fill-current' : ''}`} />
                          {p.like_count}
                        </button>
                        <span className="flex items-center gap-2 text-sm text-slate-500">
                          <MessageCircle className="w-5 h-5" />
                          {p.comments.length}
                        </span>
                      </div>
                      
                      {/* Comments Section */}
                      {p.comments.length > 0 && (
                        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 space-y-3">
                          {p.comments.map((c) => (
                            <div key={c.id} className="flex gap-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-slate-600 font-medium text-xs">
                                  {c.user_name.charAt(0)}
                                </span>
                              </div>
                              <div className="flex-1 bg-white p-3 rounded-xl">
                                <p className="font-medium text-sm text-slate-900">{c.user_name}</p>
                                <p className="text-sm text-slate-700">{renderContentWithLinks(c.content)}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(c.created_at).toLocaleDateString('en-IN', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Comment */}
                      <div className="p-4 border-t border-slate-100 flex gap-3">
                        <Input
                          data-testid={`comment-input-${p.id}`}
                          placeholder="Write a comment..."
                          value={commentInputs[p.id] || ""}
                          onChange={(e) => setCommentInputs(prev => ({ ...prev, [p.id]: e.target.value }))}
                          onKeyPress={(e) => e.key === 'Enter' && handleComment(p.id)}
                          className="flex-1 h-10 rounded-xl"
                        />
                        <Button
                          data-testid={`submit-comment-${p.id}`}
                          size="icon"
                          onClick={() => handleComment(p.id)}
                          disabled={!commentInputs[p.id]?.trim() || submittingComment[p.id]}
                          className="h-10 w-10 bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Session Matches Modal */}
      <SessionMatchesModal
        open={showSessionMatches}
        onOpenChange={setShowSessionMatches}
        session={selectedSession}
      />
      
      {/* Profile Edit Modal (when user clicks Edit button) */}
      <ProfileEditModal
        open={showProfileModal}
        onOpenChange={setShowProfileModal}
        user={dashboard?.user}
        onProfileUpdated={handleProfileUpdated}
        isForcePrompt={false}
      />
      
      {/* Profile Completion Prompt (shown on login if profile incomplete) */}
      <ProfileEditModal
        open={showProfilePrompt}
        onOpenChange={setShowProfilePrompt}
        user={dashboard?.user}
        onProfileUpdated={handleProfileUpdated}
        isForcePrompt={true}
      />
    </div>
  );
};

export default UserDashboard;
